import ImageHeader from "@arelia-components/layout/ImageHeader";
import { Box, Button } from "@mui/material";
import { GridLayout } from "@arelia-components/layout/GridLayout";
import { Campaign } from "../models";
import theme from "src/theme";
import CheckFansList from "./CheckFansList";
import CampaignText from "./CampaignText";
import CampaignName from "./CampaignName";
import CampaignMessagesList from "./CampaignMessagesList";
import { PATHS } from "src/routes";
import { formatDate } from "@arelia-utils/helpers/dates";
import { useState } from "react";
import PurchaseMessagesDialog from "../../../../components/SubscriptionDetails/components/SubscriptionInfo/PurchaseMessagesDialog";
import { useBrandRegistrationStatusAPI } from "../../../../features/fans/settings/settings.api";

type Props = {
  campaign: Campaign;
  config?: { messageLimit: number, messageLimitCurrent: number, additionalMessageLimit: number };
  updateCampaign: (campaign: Campaign) => void;
  loading: boolean;
  onSend: () => void;
}

export function CampaignDetailsPresenter({ campaign, config, updateCampaign, loading, onSend }: Props) {
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const { data: registrationStatus } = useBrandRegistrationStatusAPI(true, false);

  const twilioRegistrationCompleted = registrationStatus?.completed === true;

  const handleOpenModal = () => setPurchaseModalOpen(true);
  const handleCloseModal = () => setPurchaseModalOpen(false);

  const updateFans = (fans: string[]) => {
    updateCampaign({
      ...campaign,
      fans
    });
  };

  const updateName = (name: string) => {
    updateCampaign({
      ...campaign,
      name
    });
  };

  const updateText = (text: string, date: Date | null) => {
    updateCampaign({
      ...campaign,
      text,
      scheduledAt: date ? date.toISOString() : undefined
    });
  };

  const availableMessages = ((config?.messageLimitCurrent ?? 0) + (config?.additionalMessageLimit ?? 0));

  let alertText: string = '';
  if (campaign.scheduledAt) {
    alertText = `Scheduled to send to ${campaign.fans.length} fans at ${formatDate(new Date(campaign.scheduledAt), "MM/dd/yy hh:mm aa")}`;
  } else if (campaign.lastSentAt && campaign.lastSentCount && campaign.lastSentCount > 0) {
    alertText = `Sent to ${campaign.lastSentCount} fans at ${formatDate(campaign.lastSentAt, "MM/dd/yy hh:mm aa")}`;
  }

  return (
    <Box sx={styles.wrapperBox}>
      <ImageHeader
        title={<CampaignName initialName={campaign.name} onSave={updateName} />}
        backTo={{
          label: "back to campaigns",
          path: PATHS.CAMPAIGN_LIST
        }}
        sx={styles.header}
        dashboard
      ></ImageHeader>
      <Box p={5} pt={2}>
        <Box sx={styles.actionsBox}>
          {twilioRegistrationCompleted && (
            <>
              <Button variant="contained" color="secondary" onClick={handleOpenModal}>
                Buy More Messages
              </Button>
              <Box>
                <b>{availableMessages.toLocaleString()}</b>&nbsp; SMS messages are available this month.
              </Box>
            </>
          )}
        </Box>
        <GridLayout customColumns={'repeat(2, 1fr)'} customRows={1}>
          <CheckFansList checkedFans={campaign.fans} saveCheckedFans={updateFans} />
          <CampaignText
            initialText={campaign.text}
            loading={loading}
            sendNowEnabled={!loading && campaign.fans.length > 0}
            alertText={alertText}
            scheduledDate={campaign.scheduledAt}
            wasSent={!!campaign.lastSentAt && (campaign.lastSentCount ?? 0) > 0}
            isRegistrationCompleted={campaign.isRegistrationCompleted}
            isLimitExceeded={availableMessages <= 0}
            onSave={updateText}
            onSend={onSend} />
        </GridLayout>
        <CampaignMessagesList campaignId={campaign.id} />
      </Box>
      {purchaseModalOpen && <PurchaseMessagesDialog onClose={handleCloseModal} />}
    </Box>
  );
}

const styles = {
  wrapperBox: {
    bgcolor: theme.palette.background.default,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '34px',
    overflowY: 'auto',
  },
  header: {
    background: "none",
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: "-35px",
      left: "-35px",
      right: 0,
      bottom: 0,
      backgroundPosition: 'center',
      backgroundSize: '130% auto',
      filter: 'blur(25px)',
      zIndex: 0
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: "0",
      left: "0",
      right: 0,
      bottom: 0,
      background: "linear-gradient(154deg, #00121C 14.26%, #00475E 83.68%)",
      opacity: 0.6,
      zIndex: 1
    }
  },
  actionsBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'right',
    gap: '16px',
    marginBottom: '16px'
  }
}
